/**
 * CFF / pricing preferences sub-FAB.
 *
 * Opens a MiniModal that lets the user pick:
 *   - the display currency (CHF / EUR),
 *   - the active CFF subscription (none / half-fare / GA),
 *   - extra CFF options (discounted tickets / first class).
 *
 * Burst integration (anchor resolution, close on open, a11y) is
 * provided by `SubFloatingAction`. An `iconFilter` is forwarded so the
 * monochrome `ChangeIcon` asset is recoloured to match the other FABs
 * without duplicating the SVG.
 */

import React, { useCallback, useMemo } from "react";
import CffFirstClassIcon from "../../../../image/icons/pricing_prefs/CffFirstClassIcon.svg";
import ChangeIcon from "../../../../image/icons/pricing_prefs/ChangeIcon.svg";
import CurrencyChfOption from "../../../../image/icons/pricing_prefs/CurrencyChfOption.svg";
import CurrencyEurOption from "../../../../image/icons/pricing_prefs/CurrencyEurOption.svg";
import SwissReductionCardGA from "../../../../image/icons/pricing_prefs/SwissReductionCardGA.svg";
import SwissReductionCardHalfFare from "../../../../image/icons/pricing_prefs/SwissReductionCardHalfFare.svg";
import SwissReductionCardNone from "../../../../image/icons/pricing_prefs/SwissReductionCardNone.svg";
import SwissSuperSaverIcon from "../../../../image/icons/pricing_prefs/SwissSuperSaverIcon.svg";
import { pricingPrefsSlice, usePersistedSlice } from "../../../../store/persistence";
import { type SwissReductionCard } from "../../../../types/GlobalContext";
import ImageOptionSelector, { type ImageSelectorOption } from "../../../MiniModal/ImageOptionSelector";
import MiniModalSection from "../../../MiniModal/MiniModalSection";
import { BURST_SUB_FAB_GREEN_FILTER } from "../../floatingAction.configuration";
import SubFloatingAction from "./SubFloatingAction";

/** Supported currency option ids exposed in the selector. */
type CurrencyOptionId = "CHF" | "EUR";

/** Supported subscription option ids exposed in the selector. */
type SubscriptionOptionId = "none" | "half-fare" | "ga";

/** Supported extra-option ids exposed in the selector. */
type CffOptionId = "swissSuperSaver" | "firstClass";

/** Currency options (CHF vs EUR). */
const CURRENCY_OPTIONS: readonly ImageSelectorOption[] = [
    {
        "id":       "CHF",
        "label":    "Franc suisse",
        "imageSrc": CurrencyChfOption,
        "imgCover": false,
    },
    {
        "id":       "EUR",
        "label":    "Euro",
        "imageSrc": CurrencyEurOption,
        "imgCover": false,
    },
];

/** Subscription options (none / half-fare / GA). */
const CFF_SUBSCRIPTION_OPTIONS: readonly ImageSelectorOption[] = [
    {
        "id":       "none",
        "label":    "Aucun",
        "imageSrc": SwissReductionCardNone,
        "imgCover": false,
    },
    {
        "id":       "half-fare",
        "label":    "Demi-tarif",
        "imageSrc": SwissReductionCardHalfFare,
        "imgCover": false,
    },
    {
        "id":       "ga",
        "label":    "Abo. général",
        "imageSrc": SwissReductionCardGA,
        "imgCover": false,
    },
];

/** Extra CFF options (discounted tickets / first class). */
const CFF_OPTIONS: readonly ImageSelectorOption[] = [
    {
        "id":       "swissSuperSaver",
        "label":    "Billets dégriffé",
        "imageSrc": SwissSuperSaverIcon,
        "imgCover": false,
        "tooltip":  `
        <div style="text-align: center; font-weight: bold;">Offre permettant d'économiser jusqu'à 50% sur un trajet spécifique.</div>
        <br />
        <ul>
        <li><strong>Contrainte :</strong> Valable uniquement pour le trajet spécifique sélectionné (horaire fixe).</li>
        <li><strong>Disponibilité :</strong> Quantité limitée, non échangeable et non remboursable.</li>
        </ul>`,
    },
    {
        "id":       "firstClass",
        "label":    "1ère classe",
        "imageSrc": CffFirstClassIcon,
        "imgCover": false,
    },
];

/**
 * Type guard for `CurrencyOptionId` values.
 *
 * @param value Candidate string from the selector.
 * @returns `true` when `value` is a supported currency id.
 */
const isCurrencyOptionId = ( value: string ): value is CurrencyOptionId => {
    return value === "CHF" || value === "EUR";
};

/**
 * Type guard for `SubscriptionOptionId` values.
 *
 * @param value Candidate string from the selector.
 * @returns `true` when `value` is a supported subscription id.
 */
const isSubscriptionOptionId = ( value: string ): value is SubscriptionOptionId => {
    return value === "none" || value === "half-fare" || value === "ga";
};

/** Props exposed by the Pricing sub-FAB. */
export interface PricingPrefsSubFloatingActionProps {

    /** When `true`, use the dark palette variants. */
    "isDarkUi": boolean;

    /** Optional listener fired whenever the MiniModal open state changes. */
    "onOpenStateChange"?: ( isOpen: boolean ) => void;
}

/**
 * Render the pricing prefs sub-FAB with its MiniModal panel.
 *
 * @param props Component props. See `PricingPrefsSubFloatingActionProps`.
 * @returns The configured `SubFloatingAction` with the pricing MiniModal.
 */
const PricingPrefsSubFloatingAction: React.FC<PricingPrefsSubFloatingActionProps> = ( {
    isDarkUi,
    onOpenStateChange,
} ) => {
    const [ pricingPrefs, setPricingPrefs ] = usePersistedSlice( pricingPrefsSlice );

    const swissReductionCard: SwissReductionCard = pricingPrefs.swissReductionCard;
    const swissSuperSaver: boolean = pricingPrefs.swissSuperSaver;
    const categoryOfService: string | null = pricingPrefs.categoryOfService;
    const isEuro = pricingPrefs.displayCurrency === "EUR";

    /**
     * Validate and propagate a currency change.
     *
     * @param nextValue Candidate currency id from the selector.
     */
    const handleCurrencyChange = useCallback( ( nextValue: string ): void => {
        if ( !isCurrencyOptionId( nextValue ) ) {
            return;
        }

        setPricingPrefs( ( previous ) => ( {
            ...previous,
            "displayCurrency": nextValue,
        } ) );
    }, [ setPricingPrefs ] );

    const currentSubscriptionValue = useMemo<SubscriptionOptionId>( () => {
        return swissReductionCard;
    }, [ swissReductionCard ] );

    /**
     * Validate and propagate a subscription change.
     *
     * @param nextValue Candidate subscription id from the selector.
     */
    const handleSubscriptionChange = useCallback( ( nextValue: string ): void => {
        if ( !isSubscriptionOptionId( nextValue ) ) {
            return;
        }
        setPricingPrefs( ( previous ) => ( {
            ...previous,
            "swissReductionCard": nextValue,
        } ) );
    }, [ setPricingPrefs ] );

    const cffOptionsValue = useMemo<readonly string[]>( () => {
        const selectedOptions: CffOptionId[] = [];
        if ( swissSuperSaver === true ) {
            selectedOptions.push( "swissSuperSaver" );
        }
        if ( categoryOfService !== null ) {
            selectedOptions.push( "firstClass" );
        }

        return selectedOptions;
    }, [ categoryOfService, swissSuperSaver ] );

    /**
     * Validate and propagate a change in the extra-options selection.
     *
     * @param nextValue Selected option ids from the selector.
     */
    const handleCffOptionsChange = useCallback( ( nextValue: readonly string[] ): void => {
        const selectedOptions = new Set( nextValue );

        setPricingPrefs( ( previous ) => ( {
            ...previous,
            "swissSuperSaver":   selectedOptions.has( "swissSuperSaver" ),
            "categoryOfService": selectedOptions.has( "firstClass" ) ? "FIRST_CLASS" : null,
        } ) );
    }, [ setPricingPrefs ] );

    return (
        <SubFloatingAction
            ariaLabel="Parametres CFF"
            iconAlt="CFF settings"
            iconFilter={BURST_SUB_FAB_GREEN_FILTER}
            iconSizePx={34}
            iconSrc={ChangeIcon}
            isDarkUi={isDarkUi}
            kind="modal"
            onOpenStateChange={onOpenStateChange}
        >
            <MiniModalSection
                isDarkUi={isDarkUi}
                title="Devise"
            >
                <ImageOptionSelector
                    columns={2}
                    isDarkUi={isDarkUi}
                    onChange={handleCurrencyChange}
                    options={CURRENCY_OPTIONS}
                    selectionType="radio"
                    value={isEuro ? "EUR" : "CHF"}
                />
            </MiniModalSection>
            <MiniModalSection
                isDarkUi={isDarkUi}
                title="Abonnements CFF"
            >
                <ImageOptionSelector
                    isDarkUi={isDarkUi}
                    onChange={handleSubscriptionChange}
                    options={CFF_SUBSCRIPTION_OPTIONS}
                    selectionType="radio"
                    value={currentSubscriptionValue}
                />
            </MiniModalSection>
            <MiniModalSection
                isDarkUi={isDarkUi}
                title="Options CFF"
            >
                <ImageOptionSelector
                    columns={2}
                    isDarkUi={isDarkUi}
                    onChange={handleCffOptionsChange}
                    options={CFF_OPTIONS}
                    selectionType="checkbox"
                    value={cffOptionsValue}
                />
            </MiniModalSection>
        </SubFloatingAction>
    );
};

export default PricingPrefsSubFloatingAction;
